import { useHistory } from 'react-router-dom';
import { useEffect, useState } from 'react';
import classNames from 'classnames';

import { Alert } from '../../components/Alert/Alert';
import { Button } from '../../components/Button/Button';
import { Checkbox } from '../../components/Checkbox/Checkbox';
import { H3 } from '../../components/H3/H3';
import { Header } from '../../components/Header/Header';
import { OrderSummary } from '../../components/OrderSummary/OrderSummary';
import { OverlaySpinner } from '../../components/OverlaySpinner/OverlaySpinner';
import { Panel } from '../../components/Panel/Panel';
import { Status } from '../../types';
import { useStore } from '../../store';
import * as api from '../../services/api';
import { Form } from 'react-bootstrap';
import styles from './AdditionalInfo.module.scss';
import { track } from '../../util';
import { BRAND_COLORS } from '../../config';
import swal from 'sweetalert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileSignature, faTag, faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons';

export function AdditionalInfo() {
  const orderId = useStore((state) => state.orderId);
  const acceptBookingDetails = useStore((state) => state.acceptBookingDetails);
  const acceptInsuranceConditions = useStore((state) => state.acceptInsuranceConditions);
  const acceptTermConditions = useStore((state) => state.acceptTermConditions);
  const acceptNoDangerousGoods = useStore((state) => state.acceptNoDangerousGoods);
  const acceptReadFinancialServiceGuide = useStore((state) => state.acceptReadFinancialServiceGuide);
  const payPalPaymentGateway = useStore((state) => state.payPalPaymentGateway);
  const stripePaymentGateway = useStore((state) => state.stripePaymentGateway);
  const coinbasePaymentGateway = useStore((state) => state.coinbasePaymentGateway);
  const reCreateOrder = useStore((state) => state.reCreateOrder);
  const couponCode = useStore((state) => state.couponCode);
  const customOrder = useStore((state) => state.customOrder);
  const noPrinter = useStore((state) => state.noPrinter);

  const setCurrentStep = useStore((state) => state.setCurrentStep);
  const setAcceptBookingDetails = useStore((state) => state.setAcceptBookingDetails);
  const setAcceptInsuranceConditions = useStore((state) => state.setAcceptInsuranceConditions);
  const setAcceptReadFinancialServiceGuide = useStore((state) => state.setAcceptReadFinancialServiceGuide);
  const setAcceptTermConditions = useStore((state) => state.setAcceptTermConditions);
  const setAcceptNoDangerousGoods = useStore((state) => state.setAcceptNoDangerousGoods);
  const emails = useStore((state) => state.emails);
  const addEmail = useStore((state) => state.addEmail);
  const removeEmail = useStore((state) => state.removeEmail);
  const editEmail = useStore((state) => state.editEmail);
  const setPayPalPaymentGateway = useStore((state) => state.setPayPalPaymentGateway);
  const setStripePaymentGateway = useStore((state) => state.setStripePaymentGateway);
  const setCoinbasePaymentGateway = useStore((state) => state.setCoinbasePaymentGateway);
  const setOrderId = useStore((state) => state.setOrderId);
  const setReCreateOrder = useStore((state) => state.setReCreateOrder);
  const setCouponCode = useStore((state) => state.setCouponCode);
  const setCouponDiscountPercentage = useStore((state) => state.setCouponDiscountPercentage);
  const [additionalEmailError, setAdditionalEmailError] = useState('');
  const [couponCodeError, setCouponCodeError] = useState('');
  const [emailFieldErrors, setEmailFieldErrors] = useState<Record<number, string>>({});

  // Validation function for Confirm Booking button
  const isConfirmBookingDisabled = () => {
    // Check if all required terms and conditions are accepted
    return !(
      acceptBookingDetails &&
      acceptTermConditions &&
      acceptNoDangerousGoods &&
      acceptReadFinancialServiceGuide
    );
  };

  const history = useHistory();
  const [status, setStatus] = useState<Status>('idle');
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const onEmailChange = (index: number, value: string) => {
    editEmail(index, 'email', value);
    if (emailFieldErrors[index]) {
      setEmailFieldErrors((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const onAddEmail = () => {
    const nextErrors: Record<number, string> = {};
    emails.forEach((entry, index) => {
      const value = (entry?.email ?? '').trim();
      if (!value || !emailRegex.test(value)) {
        nextErrors[index] = 'Please enter a valid email address';
      }
    });

    if (Object.keys(nextErrors).length) {
      setEmailFieldErrors(nextErrors);
      return;
    }

    setEmailFieldErrors({});
    addEmail();
  };

  const onRemoveEmail = (index: number) => {
    removeEmail(index);
    setEmailFieldErrors({});
  };

  const save = () => {
    (async () => {
      setCouponCodeError('');
      setAdditionalEmailError('');
      try {
        let hashId: any;
        hashId = orderId;
        setStatus('in-progress');
        if(reCreateOrder){
          const reCreateOrderRecord = await api.post<any>(`/order/${orderId}/reCreateOrder`, {});
          hashId = reCreateOrderRecord.id;
          setOrderId(reCreateOrderRecord.id);
          setReCreateOrder(false);
        }
        await api.csrf();

        const response = await api.patch<any>(`/order/${hashId}/additional-info`, {
          acceptBookingDetails: acceptBookingDetails,
          acceptInsuranceConditions: acceptInsuranceConditions,
          acceptTermConditions: acceptTermConditions,
          acceptAttachment: false,
          acceptNoDangerousGoods: acceptNoDangerousGoods,
          acceptReadFinancialServiceGuide:acceptReadFinancialServiceGuide,
          emails: emails,
        });
        setPayPalPaymentGateway(response.PayPal);
        setStripePaymentGateway(response.Stripe);
        setCoinbasePaymentGateway(response.Coinbase);
        if(response.coupon_discount_percentage){
          setCouponDiscountPercentage(response.coupon_discount_percentage);
        }
        
        track('app-additional-info', {
          additionalEmails:emails
        });
        if(customOrder){
          history.push(`/order-complete?ref=${hashId}&new=`);
        }else{
          history.push('/payment');
        }
      } catch (e: any) {
          setAdditionalEmailError(e?.response?.data?.error ?? '');
          setStatus('error');
      }
    })();
  };

  const validateCoupon = () => {
    if(couponCode){
      setCouponCodeError('');
      setAdditionalEmailError('');
    (async () => {
      try {
        let hashId: any;
        hashId = orderId;
        setStatus('in-progress');
        await api.csrf();

        const couponResponse = await api.patch<any>(`/order/${hashId}/coupon`, {
          couponCode: couponCode,
        });

        if(couponResponse.coupon_discount_percentage){
          setCouponDiscountPercentage(couponResponse.coupon_discount_percentage);
          await swal({
            title: "Coupon applied successfully!",
            icon: "success",
            confirmButtonText: "Ok",
        } as any);
          setStatus('idle');
        }
      } catch (e: any) {
        if(e?.response?.data?.type){
          setCouponCodeError(e?.response?.data?.error ?? '');
        }
        setStatus('error');
      }
    })()};
  }

  useEffect(() => {
    if (!orderId) {
      history.push('/package-details');
      return;
    }

    setCurrentStep(3);
  }, [setCurrentStep, orderId, history]);

  let lastTerm = 'YES - I have read the prohibited/restricted product list and declare that this shipment does not contain dangerous goods';
  lastTerm = lastTerm.replace('prohibited/restricted product list', '<a href="">prohibited/restricted product list</a>')
  return (
    <>
      <Header />
      <OverlaySpinner enabled={status === 'in-progress'} />
      <div className={styles.page}>
      <div className={styles.content}>
      <OrderSummary
        className={styles.orderSummary}
        packageDetailsEditEnabled={true}
        quoteEditEnabled={true}
        addressDetailsEditEnabled={true}
        variant="payment-package-card"
      />
      <Panel className={classNames(styles.panel, styles.termsPanel)}>
        <H3 className={styles.sectionHeading}>
          <span className={styles.sectionTitleIcon}>
            <FontAwesomeIcon icon={faFileSignature} />
          </span>
          Terms &amp; Conditions
        </H3>
        {/* <Checkbox
          name="acceptInsuranceConditions"
          className={styles.input}
          value={acceptInsuranceConditions}
          onChange={setAcceptInsuranceConditions}
          label="YES - I have read, understood &amp; agree to the road express transit insurance conditions."
        /> */}
        <Checkbox
          name="acceptBookingDetails"
          className={classNames(styles.input, styles.termsCheckbox)}
          value={acceptBookingDetails}
          onChange={setAcceptBookingDetails}
          label="I confirm that all details entered in the booking are accurate and truthful to the best of my knowledge. Extra charges will apply for any incorrectly declared information including but not limited to weights, dimensions, address type and/or package types."
        />
        <Checkbox
          name="acceptTermConditions"
          className={classNames(styles.input, styles.termsCheckbox)}
          value={acceptTermConditions}
          onChange={setAcceptTermConditions}
          label={
            <div>
              YES - I have read, understood &amp; agree to the{' '}
              <a style={{ color: BRAND_COLORS.primary }} href="https://fastcourier.com.au/terms-conditions-2" target="_blank" rel="noreferrer">
                Terms &amp; Conditions.
              </a>
            </div>
          }
        />
        <Checkbox
          name="acceptNoDangerousGoods"
          className={classNames(styles.input, styles.termsCheckbox)}
          value={acceptNoDangerousGoods}
          onChange={setAcceptNoDangerousGoods}
          label={
            <div>
            YES - I have read the <a style={{ color: BRAND_COLORS.primary }} href="https://fastcourier.com.au/prohibited-restricted-list" target="_blank" rel="noreferrer">
             prohibited/restricted product list
             </a> and declare that this shipment does not contain dangerous goods
             
           </div>
          }
        />
        <Checkbox
          name="acceptReadFinancialServiceGuide"
          className={classNames(styles.input, styles.termsCheckbox)}
          value={acceptReadFinancialServiceGuide}
          onChange={setAcceptReadFinancialServiceGuide}
          label={
            <div>
            YES - I/We have read and understood the  
             <a style={{ color: BRAND_COLORS.primary }} href="https://fastcourier.com.au/extended-liability-terms-conditions"> Policy Terms and Conditions</a> and I accept the option I have elected."
              
            </div>
          }
        />
        
        <Checkbox
          name="acceptAll"
          className={classNames(styles.input, styles.termsCheckbox, styles.agreeAll)}
          value={acceptInsuranceConditions && acceptBookingDetails && acceptTermConditions && acceptNoDangerousGoods && acceptReadFinancialServiceGuide}
          onChange={() => {
            setAcceptInsuranceConditions(true);
            setAcceptBookingDetails(true);
            setAcceptTermConditions(true);
            setAcceptNoDangerousGoods(true);
            setAcceptReadFinancialServiceGuide(true);
          }}
          label="Agree to all of the above"
        />
      </Panel>
      <div className={classNames(styles.infoCard, styles.couponCard)}>
        <H3 className={styles.sectionHeading}>
          <span className={styles.sectionTitleIcon}>
            <FontAwesomeIcon icon={faTag} />
          </span>
          Add Coupon code for discount
        </H3>
        <div className={classNames(styles.formRow, styles.couponRow, styles.rowBox)}>
          <div className={classNames(styles.fieldLabel, styles.rowLabel)}>Coupon</div>
          <Form className={classNames(styles.fieldForm, styles.couponForm, styles.rowInputWrap)}>
            <Form.Group className={styles.inputField} controlId="exampleForm.ControlTextarea1">
              <Form.Control
                type="text"
                placeholder="Coupon"
                name="couponCode"
                className={classNames(styles.textInput, styles.rowInput)}
                onChange={(e) => setCouponCode(e.target.value)}
              />
            </Form.Group>
          </Form>
        </div>
        <div className={classNames(styles.buttonRow, styles.couponButtonRow)}>
          <Button className={styles.compactActionButton} type="ghost" size="small" onClick={validateCoupon}>
            Apply coupon
          </Button>
        </div>
      </div>

      <div className={styles.infoCard}>
        <H3 className={styles.sectionHeading}>
          <span className={styles.sectionTitleIcon}>
            <FontAwesomeIcon icon={faEnvelopeOpenText} />
          </span>
          Additional Emails to send Labels &amp; Invoices to
        </H3>
        <div className={styles.emailList}>
          {emails.map((email, i) => (
            <div className={styles.emailItem} key={`email-${i}`}>
              <div className={classNames(styles.formRow, styles.emailRow, styles.rowBox)}>
                <div className={classNames(styles.fieldLabel, styles.rowLabel)}>Email</div>
                <Form className={classNames(styles.fieldForm, styles.emailForm, styles.rowInputWrap)}>
                  <Form.Group className={styles.inputField} controlId={`formEmail${i}`}>
                    <Form.Control
                      name="email"
                      value={email.email}
                      onChange={(e) => onEmailChange(i, e.target.value)}
                      type="text"
                      placeholder="Email"
                      className={classNames(styles.textInput, styles.rowInput)}
                    />
                  </Form.Group>
                </Form>
                {emails.length > 1 && (
                  <button className={classNames(styles.remove, styles.removeInline)} type="button" onClick={() => onRemoveEmail(i)}>
                    Remove
                  </button>
                )}
              </div>
              {emailFieldErrors[i] && <span className={classNames(styles.fieldError, styles.rowError)}>{emailFieldErrors[i]}</span>}
            </div>
          ))}
          <div className={classNames(styles.buttonRow, styles.addEmailRow)}>
            <Button className={styles.compactActionButton} type="ghost" size="small" onClick={onAddEmail}>
              Add email
            </Button>
          </div>
        </div>
      </div>
      {couponCodeError && (
        <Alert className={styles.alert} type="error">
          {couponCodeError}
        </Alert>
      )}
      {status === 'error' && !additionalEmailError && !couponCodeError && (
        <Alert className={styles.alert} type="error">
          Please ensure to accept the Terms &amp; Conditions
        </Alert>
      )}
      {status === 'error' && additionalEmailError && (
        <Alert className={styles.alert} type="error">
          {additionalEmailError}
        </Alert>
      )}
      <div className={styles.toolbar}>
        <Button onClick={save} disabled={isConfirmBookingDisabled()}>
          {customOrder ? 'Submit Booking' : 'Confirm Booking'}
        </Button>
      </div>
      </div>
      </div>
    </>
  );
}
